// oxlint-disable i18next/no-literal-string
import React, { FC } from "react";
import Grid from "@mui/material/Grid2";
import { Box, Typography } from "@mui/material";
import {
  RateBuilderTreeFormFields,
  RateItemSchemaFormFieldsInfer,
} from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { RateType, UnitName } from "@sinch/core/entities/serverEnums";
import { useFormat } from "@sinch/core/hooks/format";
import { useRateComponent } from "@sinch/components/application/RateComponent/UseRateComponent";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { useUnitType } from "@sinch/components/application/Rate/useUnitType";
import { useMuiController } from "@sinch/components";
import { PercentsInput } from "@sinch/components/form/PercentsInput/PercentsInput";
import { CurrencyInput } from "@sinch/components/form/CurrencyInput/CurrencyInput";
import { isNotNil } from "ramda-adjunct";
import { useTranslation } from "react-i18next";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { isBreakPenaltyUnit } from "@sinch/components/application/Rate/useIntervalType";
import { RateIntervals } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.graphql";
import { RateIntervalsQuery } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.types";

export const RateOverview: FC<{ rate: RateItemSchemaFormFieldsInfer["rate"]; rowId: string }> = ({ rate, rowId }) => {
  const { t } = useTranslation();
  const { num } = useFormat();
  const { getLabel } = useRateComponent();
  const { mode, rateType } = useRateBuilderSettings();
  const { getUnitName } = useUnitType(rateType);
  const { data: intervalsData } = useQueryHandler<RateIntervalsQuery>(RateIntervals, { ratelistType: rateType });

  // Break penalty rows are valued from the rate active during the missed break, so their amount is
  // calculated on evaluation - the stored zero is a placeholder, not a real rate.
  const isBreakPenalty = isBreakPenaltyUnit(intervalsData?.rateIntervals, rate?.unit);

  // Besides an explicit percentage rate, units measured in currency (eg. percentage of the ratelist
  // total amount) are percentages as well - the same rule as in the editable RateInput.
  const isPercentage =
    rate?.type === RateType.Percentage ||
    (isNotNil(rate?.unit.type) && getUnitName(rate.unit.type) === UnitName.Currency);

  const amount = isPercentage ? num.percent(rate?.amount ?? 0) : num.currency(rate?.amount ?? 0);
  const base = rate?.baseComponent;

  return (
    <Grid
      container
      spacing={0.5}
      sx={{
        height: "100%",
        width: "100%",
        alignItems: "flex-end",
        justifyContent: "flex-end",
      }}
      wrap="nowrap"
    >
      {base && (
        <Grid
          size="grow"
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textAlign: "right",
          }}
        >
          {getLabel(base)}
        </Grid>
      )}

      {base && <Grid size="auto">x</Grid>}
      <Grid size={rate && mode === "override" && !isBreakPenalty ? 6 : "auto"}>
        {isBreakPenalty ? (
          <Typography sx={{ color: "text.secondary" }} variant="descriptionSemi">
            {t("RateBuilder.calculated")}
          </Typography>
        ) : rate && mode === "override" ? (
          <OverrideInput isPercentage={isPercentage} rowId={rowId} />
        ) : (
          isNotNil(rate) && <Typography variant="descriptionSemi">{amount}</Typography>
        )}
      </Grid>
    </Grid>
  );
};

const OverrideInput = ({ isPercentage, rowId }: { isPercentage: boolean; rowId: string }) => {
  const { field } = useMuiController<RateBuilderTreeFormFields, `Rate.${string}.rate.amount`>({
    name: `Rate.${rowId}.rate.amount`,
    disableAsReadOnly: true,
  });
  return (
    <Box>
      {isPercentage ? (
        <PercentsInput {...field} size="small" value={field.value ?? 0} />
      ) : (
        <CurrencyInput {...field} size="small" value={field.value ?? 0} />
      )}
    </Box>
  );
};
